// src/pages/Billing.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StandardPage from '../components/Layout/StandardPage';
import {
  Table,
  Tag,
  Space,
  Typography,
  message,
  Tooltip,
  Select,
  Input,
  Button,
  Divider,
  Popover,
  Switch,
  InputNumber,
  DatePicker,
  Card,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SearchOutlined, CalendarOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

import {
  getBillingStatus,
  setBillingMonth,
  type BillingRow,
  type BillingMonth,
} from '../services/billing';

const { Text } = Typography;

type RowVM = BillingRow & {
  totalYear: number; // somma importi (€) dei 12 mesi
};

const monthShort = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const buildYearOptions = (center = dayjs().year(), past = 4, future = 4) =>
  Array.from({ length: past + future + 1 }, (_, i) => center - past + i).map((y) => ({
    value: y,
    label: String(y),
  }));

const getCell = (row: BillingRow, monthIndex0: number): BillingMonth | undefined => {
  const m = monthIndex0 + 1;
  return row.months?.[m];
};

const firstDayOfMonthIso = (year: number, monthIndex0: number) =>
  dayjs().year(year).month(monthIndex0).date(1).startOf('day').toISOString();

/** --------- NUOVO: struttura per più pagamenti ---------- */
type PaymentLine = {
  id?: number;
  amountEuro: number;
  paidAtIso: string;
  note?: string | null;
};

// estrae righe da cella backend (retro-compat)
// - se esiste cell.payments usa quello
// - altrimenti, se amountEuro > 0 crea una singola riga legacy
const extractPaymentsFromCell = (cell: BillingMonth | undefined, year: number, idx0: number): PaymentLine[] => {
  if (!cell) return [];
  const anyCell = cell as any;
  if (Array.isArray(anyCell.payments) && anyCell.payments.length) {
    return anyCell.payments.map((p: any) => ({
      id: p.id,
      amountEuro: Number(p.amountEuro || 0),
      paidAtIso: p.paidAt || firstDayOfMonthIso(year, idx0),
      note: p.note ?? null,
    }));
  }
  const legacyAmt = Number(cell.amountEuro || 0);
  if (legacyAmt > 0) {
    return [{
      amountEuro: legacyAmt,
      paidAtIso: cell.paidAt || firstDayOfMonthIso(year, idx0),
      note: cell.note ?? null,
    }];
  }
  return [];
};

const sumPayments = (list: PaymentLine[]) =>
  list.reduce((acc, p) => acc + (Number(p.amountEuro || 0)), 0);

const latestPaidAtIso = (list: PaymentLine[]) => {
  if (!list.length) return null;
  return list
    .map(p => p.paidAtIso)
    .filter(Boolean)
    .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())
    .at(-1) || null;
};

type EditDraft = {
  paid: boolean;          // derivato da somma > 0 (ma lasciamo switch manuale)
  payments: PaymentLine[]; // più righe
  note: string | null;     // nota del mese (separata dalle note riga)
};

const BillingPage: React.FC = () => {
  const [year, setYear] = useState<number>(dayjs().year());
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<RowVM[]>([]);

  const [qCustomer, setQCustomer] = useState<string>('');

  // popover per cella mese
  const [openKey, setOpenKey] = useState<string | null>(null); // `${customerId}-${month}`
  const [editDraft, setEditDraft] = useState<EditDraft>({
    paid: false,
    payments: [],
    note: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBillingStatus(year);
      const mapped: RowVM[] = (data || []).map((r) => {
        let total = 0;
        for (let i = 1; i <= 12; i++) {
          const cell = r.months?.[i] as any;
          // se il backend già manda payments, sommo quelli; altrimenti legacy amountEuro
          const monthTotal = Array.isArray(cell?.payments)
            ? cell.payments.reduce((acc: number, p: any) => acc + (Number(p.amountEuro || 0)), 0)
            : Number(cell?.amountEuro || 0);
          total += monthTotal;
        }
        return { ...r, totalYear: total };
      });
      setRows(mapped);
    } catch (e: any) {
      message.error(e?.message || 'Errore nel caricamento billing');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRows = useMemo(() => {
    const q = qCustomer.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.customerName || '').toLowerCase().includes(q));
  }, [rows, qCustomer]);

  const openEditorFor = (row: RowVM, monthIndex0: number) => {
    const cell = getCell(row, monthIndex0);
    const key = `${row.customerId}-${monthIndex0 + 1}`;
    const payments = extractPaymentsFromCell(cell, year, monthIndex0);

    setOpenKey(key);
    setEditDraft({
      paid: !!(payments.length && sumPayments(payments) > 0),
      payments,
      note: cell?.note ?? null,
    });
  };

  const closeEditor = () => setOpenKey(null);

  const handleSaveCell = async (row: RowVM, monthIndex0: number) => {
    const month = monthIndex0 + 1;

    // normalizza: se switch "paid" è OFF, azzero i pagamenti
    const normalizedPayments = editDraft.paid ? editDraft.payments.filter(p => (p.amountEuro || 0) > 0) : [];
    const total = sumPayments(normalizedPayments);
    const latest = latestPaidAtIso(normalizedPayments);

    try {
      // invio compat + estensione payments (se backend la supporta la userà, altrimenti la ignora)
      const payload: any = {
        paid: !!total,
        amountEuro: total || null,
        note: editDraft.note ?? null,
        paidAt: latest || undefined,
        payments: normalizedPayments.map(p => ({
          id: p.id,
          amountEuro: p.amountEuro,
          paidAt: p.paidAtIso,
          note: p.note ?? null,
        })),
      };

      const updated = await setBillingMonth(row.customerId, year, month, payload);

      // aggiorno lo stato locale calcolando total per l'anno
      setRows(prev =>
        prev.map(r => {
          if (r.customerId !== row.customerId) return r;

          const nextMonths: any = { ...(r.months || {}) };
          // salvo nella cella sia i campi legacy sia l'array payments per coerenza locale
          nextMonths[month] = {
            ...(nextMonths[month] || {}),
            paid: !!total,
            amountEuro: total || null,
            note: editDraft.note ?? null,
            paidAt: latest || null,
            payments: normalizedPayments,
          };

          let totalYear = 0;
          for (let i = 1; i <= 12; i++) {
            const c: any = nextMonths[i];
            const monthTotal = Array.isArray(c?.payments)
              ? c.payments.reduce((acc: number, p: any) => acc + (Number(p.amountEuro || 0)), 0)
              : Number(c?.amountEuro || 0);
            totalYear += monthTotal;
          }
          return { ...r, months: nextMonths, totalYear };
        })
      );

      closeEditor();
      message.success('Salvato');
    } catch (e: any) {
      message.error(e?.message || 'Errore nel salvataggio');
    }
  };

  const columns: ColumnsType<RowVM> = useMemo(() => {
    const clientCol: ColumnsType<RowVM> = [
      {
        title: 'Cliente',
        dataIndex: 'customerName',
        key: 'customerName',
        fixed: 'left',
        width: 280,
        render: (v: string, r) => (
          <Space direction="vertical" size={0}>
            <Text strong>{v}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>ID #{r.customerId}</Text>
          </Space>
        ),
        sorter: (a, b) => (a.customerName || '').localeCompare(b.customerName || ''),
      },
    ];

    const totalLeftCol: ColumnsType<RowVM> = [
      {
        title: 'Totale anno (€)',
        dataIndex: 'totalYear',
        key: 'totalYear',
        width: 160,
        align: 'right' as const,
        fixed: 'left' as const,
        render: (v: number) =>
          Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        sorter: (a, b) => a.totalYear - b.totalYear,
      },
    ];

    const monthlyCols: ColumnsType<RowVM> = monthShort.map((label, idx0) => ({
      title: label,
      key: `m${idx0 + 1}`,
      width: 170,
      filters: [
        { text: 'Pagato', value: 'paid' },
        { text: 'Non pagato', value: 'unpaid' },
      ],
      onFilter: (value, record) => {
        const cell = getCell(record, idx0) as any;
        const monthTotal = Array.isArray(cell?.payments)
          ? cell.payments.reduce((acc: number, p: any) => acc + (Number(p.amountEuro || 0)), 0)
          : Number(cell?.amountEuro || 0);
        const paid = monthTotal > 0;
        if (value === 'paid') return paid;
        if (value === 'unpaid') return !paid;
        return true;
      },
      render: (_: any, r) => {
        const rawCell = getCell(r, idx0) as any;
        const total = Array.isArray(rawCell?.payments)
          ? rawCell.payments.reduce((acc: number, p: any) => acc + (Number(p.amountEuro || 0)), 0)
          : Number(rawCell?.amountEuro || 0);
        const note = rawCell?.note;
        const key = `${r.customerId}-${idx0 + 1}`;
        const isOpen = openKey === key;

        const monthStart = dayjs().year(year).month(idx0).startOf('month');
        const disabledDate = (current: Dayjs) =>
          !current || current.year() !== year || current.month() !== idx0;

        // ---------- Contenuto editor: lista pagamenti ----------
        const content = (
          <div style={{ width: 360 }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Pagamenti del mese</Text>
                <CalendarOutlined />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text style={{ width: 90 }}>Pagato</Text>
                <Switch
                  checked={editDraft.paid}
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={(v) =>
                    setEditDraft(s => ({
                      ...s,
                      paid: v,
                      payments: v ? (s.payments.length ? s.payments : [{
                        amountEuro: 0,
                        paidAtIso: firstDayOfMonthIso(year, idx0),
                        note: null,
                      }]) : []
                    }))
                  }
                />
              </div>

              {/* Lista pagamenti */}
              {editDraft.paid && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Inserisci una o più righe (importo + data).
                    </Text>
                    <Button
                      type="dashed"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        setEditDraft(s => ({
                          ...s,
                          payments: [
                            ...s.payments,
                            { amountEuro: 0, paidAtIso: firstDayOfMonthIso(year, idx0), note: null }
                          ]
                        }))
                      }
                    >
                      Aggiungi pagamento
                    </Button>
                  </div>

                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {editDraft.payments.map((p, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '120px 1fr 32px',
                          gap: 8,
                          alignItems: 'center'
                        }}
                      >
                        <InputNumber
                          min={0}
                          step={0.01}
                          precision={2}
                          prefix="€ "
                          style={{ width: '100%' }}
                          value={p.amountEuro}
                          onChange={(v) =>
                            setEditDraft(s => {
                              const list = [...s.payments];
                              const num = typeof v === 'number' ? v : Number(v ?? 0);
                              list[i] = { ...list[i], amountEuro: isFinite(num) ? num : 0 };
                              return { ...s, payments: list };
                            })
                          }
                        />
                        <DatePicker
                          format="DD/MM/YYYY"
                          value={p.paidAtIso ? dayjs(p.paidAtIso) : null}
                          onChange={(d: Dayjs | null) =>
                            setEditDraft(s => {
                              const list = [...s.payments];
                              list[i] = { ...list[i], paidAtIso: d ? d.startOf('day').toISOString() : firstDayOfMonthIso(year, idx0) };
                              return { ...s, payments: list };
                            })
                          }
                          disabledDate={disabledDate}
                          allowClear={false}
                        />
                        <Button
                          icon={<DeleteOutlined />}
                          danger
                          onClick={() =>
                            setEditDraft(s => {
                              const list = s.payments.slice();
                              list.splice(i, 1);
                              return { ...s, payments: list, paid: list.some(x => (x.amountEuro || 0) > 0) };
                            })
                          }
                        />
                        <Input.TextArea
                          rows={2}
                          placeholder="Nota (facoltativa)"
                          value={p.note ?? ''}
                          onChange={(e) =>
                            setEditDraft(s => {
                              const list = [...s.payments];
                              list[i] = { ...list[i], note: e.target.value };
                              return { ...s, payments: list };
                            })
                          }
                          style={{ gridColumn: '1 / span 3', marginTop: 6 }}
                        />
                      </div>
                    ))}
                  </Space>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <Text strong>
                      Totale: € {sumPayments(editDraft.payments).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                  </div>
                </div>
              )}

              <div>
                <Text style={{ display: 'block', marginBottom: 4 }}>Nota mese</Text>
                <Input.TextArea
                  rows={3}
                  value={editDraft.note ?? ''}
                  onChange={(e) => setEditDraft(s => ({ ...s, note: e.target.value }))}
                  placeholder="Note facoltative…"
                />
              </div>

              <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button onClick={closeEditor}>Annulla</Button>
                <Button type="primary" onClick={() => handleSaveCell(r, idx0)}>
                  Salva
                </Button>
              </Space>
            </Space>
          </div>
        );

        const cellView = (total && total > 0) ? (
          <Tag color="green" style={{ cursor: 'pointer' }}>
            € {Number(total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Tag>
        ) : (
          <Text type="secondary" style={{ cursor: 'pointer' }}>—</Text>
        );

        const maybeWithNote = note ? <Tooltip title={note}>{cellView}</Tooltip> : cellView;

        return (
          <Popover
            trigger="click"
            open={isOpen}
            onOpenChange={(next) => {
              if (next) openEditorFor(r, idx0);
              else closeEditor();
            }}
            content={content}
            placement="bottom"
          >
            <div style={{ minHeight: 32, display: 'inline-block' }}>
              {maybeWithNote}
            </div>
          </Popover>
        );
      },
      sorter: (a, b) => {
        const avRaw: any = getCell(a, idx0);
        const bvRaw: any = getCell(b, idx0);
        const av = Array.isArray(avRaw?.payments)
          ? avRaw.payments.reduce((acc: number, p: any) => acc + (Number(p.amountEuro || 0)), 0)
          : Number(avRaw?.amountEuro || 0);
        const bv = Array.isArray(bvRaw?.payments)
          ? bvRaw.payments.reduce((acc: number, p: any) => acc + (Number(p.amountEuro || 0)), 0)
          : Number(bvRaw?.amountEuro || 0);
        return av - bv;
      },
    }));

    return [...clientCol, ...totalLeftCol, ...monthlyCols];
  }, [openKey, editDraft, year]);

  const actions = [
    {
      label: 'Ricarica',
      type: 'default' as const,
      icon: <ReloadOutlined />,
      onClick: fetchData,
    },
  ];

  return (
    <StandardPage
      title="Pagamenti"
      subtitle="Stato pagamenti per cliente / mese"
      loading={loading}
      actions={actions}
    >
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Text strong>Anno:</Text>
          <Select
            value={year}
            onChange={(y) => setYear(y)}
            options={buildYearOptions(dayjs().year(), 4, 4)}
            style={{ width: 110 }}
          />
        </div>

        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Cerca cliente…"
          value={qCustomer}
          onChange={(e) => setQCustomer(e.target.value)}
          style={{ width: 260 }}
        />
      </div>

      <Divider style={{ margin: '8px 0 16px' }} />
<Card
          style={{
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
          bodyStyle={{ padding: 0 }}>
      <Table<RowVM>
        rowKey={(r) => `${r.customerId}-${year}`}
        dataSource={filteredRows}
        columns={columns}
        scroll={{ x: 1900 }}
        pagination={{  
                defaultPageSize: 8,
                pageSizeOptions: [8, 10, 20, 50, 100],
                showSizeChanger: true,
                showQuickJumper:true }}
      />
      </Card>
    </StandardPage>
  );
};

export default BillingPage;
